import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { PreggaColors } from "../../../theme/colors";
import { DataTable, TableColumn } from "../../ui/DataTable";
import { Button } from "../../ui/Button";
import { Input } from "../../ui/Input";
import { Select } from "../../ui/Select";
import { StatusBadge } from "../../ui/Badge";
import { Card } from "../../ui/Card";
import { Modal } from "../../ui/Modal";
import { useSupabasePaginatedQuery, useSupabaseQuery } from "../../../hooks";
import {
  fetchUsers,
  fetchUserById,
  fetchUserConversations,
  fetchUserBroadcasts,
  updateUserProfile,
  deleteUser,
  fetchDeletionRequests,
  approveDeletionRequest,
  rejectDeletionRequest,
  createManualSubscription,
  updateSubscription,
  type UserFilters,
} from "../../../lib/api";
import type { SubscriptionPlan } from "../../../types/database";
import type { DeletionRequest } from "../../../types/database";
import { friendlyError } from "../../../lib/errors";
import { formatTimeAgo } from "../../../lib/formatTime";
import type { UserWithProfile, ConversationWithUsers, BroadcastRequest } from "../../../types/database";
import {
  Search,
  ArrowLeft,
  X,
  Trash2,
  AlertTriangle,
  Pencil,
  Calendar,
  MessageCircle,
  Radio,
  User,
  CreditCard,
  AlertCircle,
  Copy,
  Check,
  Eye,
  Plus,
} from "lucide-react";
import { TabSelector, Tab } from "../../ui";

interface UsersViewProps {
  isMobile: boolean;
  subView?: string;
  initialTab?: "active" | "requests";
  onNavigateToSubView?: (id: string) => void;
  onGoBack?: () => void;
  onNavigateToConversation?: (conversationId: string) => void;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function toLocalDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function CopyableId({ id }: { id: string }) {
  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(id);
    toast.success("ID copied to clipboard");
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{ fontFamily: "monospace", fontSize: 12, color: PreggaColors.neutral500 }}>
        {id.slice(0, 8)}...
      </span>
      <button
        onClick={handleCopy}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 24,
          height: 24,
          borderRadius: 4,
          border: "none",
          background: "transparent",
          color: PreggaColors.neutral400,
          cursor: "pointer",
          transition: "all 0.15s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = PreggaColors.neutral100;
          e.currentTarget.style.color = PreggaColors.neutral600;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "transparent";
          e.currentTarget.style.color = PreggaColors.neutral400;
        }}
        title="Copy ID"
      >
        <Copy size={14} />
      </button>
    </div>
  );
}

function calculatePregnancyWeek(dueDate: string | null): number | null {
  if (!dueDate) return null;
  const due = new Date(dueDate);
  const now = new Date();
  const diffMs = due.getTime() - now.getTime();
  const weeksUntilDue = Math.ceil(diffMs / (7 * 24 * 60 * 60 * 1000));
  const currentWeek = 40 - weeksUntilDue;
  return currentWeek > 0 && currentWeek <= 42 ? currentWeek : null;
}

function Shimmer({ width = "100%", height = 20, borderRadius = 6 }: { width?: string | number; height?: string | number; borderRadius?: number }) {
  return (
    <div
      style={{
        width,
        height,
        borderRadius,
        background: `linear-gradient(90deg, ${PreggaColors.neutral100} 25%, ${PreggaColors.neutral50} 50%, ${PreggaColors.neutral100} 75%)`,
        backgroundSize: "200% 100%",
        animation: "shimmer 1.5s infinite",
      }}
    />
  );
}

export function UsersView({ isMobile, subView, initialTab, onNavigateToSubView, onGoBack, onNavigateToConversation }: UsersViewProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState<UserFilters>({});
  const [listTab, setListTab] = useState<"active" | "requests">(initialTab || "active");
  const [isApproving, setIsApproving] = useState<string | null>(null);
  const [isRejecting, setIsRejecting] = useState<string | null>(null);

  const {
    data: users,
    count,
    isLoading,
    error,
    page,
    pageSize,
    totalPages,
    setPage,
    refetch,
  } = useSupabasePaginatedQuery<UserWithProfile>(
    ['users', JSON.stringify(filters), searchQuery],
    (from, to) => fetchUsers(from, to, { ...filters, search: searchQuery || undefined }),
    { pageSize: 10 }
  );

  // User-initiated deletion requests (pending)
  const { data: deletionRequests, isLoading: requestsLoading, refetch: refetchRequests } = useSupabaseQuery<(DeletionRequest & { user?: UserWithProfile })[]>(
    ['deletion-requests'],
    fetchDeletionRequests
  );

  // Track removed request IDs for optimistic UI updates
  const [removedRequestIds, setRemovedRequestIds] = useState<string[]>([]);
  const filteredDeletionRequests = deletionRequests?.filter(r => !removedRequestIds.includes(r.id)) || [];

  const handleApproveRequest = async (requestId: string, userId: string) => {
    setIsApproving(requestId);
    try {
      await approveDeletionRequest(requestId, userId);
      // Optimistically remove from list immediately
      setRemovedRequestIds(prev => [...prev, requestId]);
      toast.success("User permanently deleted");
      // Refresh in background
      refetchRequests().then(() => setRemovedRequestIds([]));
      refetch();
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setIsApproving(null);
    }
  };

  const handleRejectRequest = async (requestId: string) => {
    setIsRejecting(requestId);
    try {
      await rejectDeletionRequest(requestId);
      // Optimistically remove from list immediately
      setRemovedRequestIds(prev => [...prev, requestId]);
      toast.success("Deletion request rejected - user kept active");
      // Refresh in background
      refetchRequests().then(() => setRemovedRequestIds([]));
      refetch();
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setIsRejecting(null);
    }
  };

  if (subView) {
    return (
      <UserDetailView
        userId={subView}
        isMobile={isMobile}
        onGoBack={onGoBack}
        onRefresh={refetch}
        onNavigateToConversation={onNavigateToConversation}
      />
    );
  }

  const columns: TableColumn<UserWithProfile>[] = [
    {
      key: "id",
      label: "User ID",
      render: (_, row) => <CopyableId id={row.id} />,
    },
    {
      key: "display_name",
      label: "User",
      render: (_, row) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              background: PreggaColors.primary100,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: PreggaColors.primary500,
              fontSize: 13,
              fontWeight: 600,
              overflow: "hidden",
            }}
          >
            {row.avatar_url ? (
              <img src={row.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              (row.display_name || "U").split(" ").map((n) => n[0]).join("").slice(0, 2)
            )}
          </div>
          <div>
            <div style={{ fontWeight: 500, color: PreggaColors.neutral900 }}>{row.display_name || "Unnamed"}</div>
            <div style={{ fontSize: 12, color: PreggaColors.neutral500 }}>{row.email || row.phone || "No contact"}</div>
          </div>
        </div>
      ),
    },
    {
      key: "pregnant_profiles",
      label: "Pregnancy",
      render: (_, row) => {
        const week = calculatePregnancyWeek(row.pregnant_profiles?.due_date || null);
        return week ? (
          <div>
            <div style={{ fontWeight: 500 }}>Week {week}</div>
            <div style={{ fontSize: 12, color: PreggaColors.neutral500 }}>
              Due: {formatDate(row.pregnant_profiles!.due_date!)}
            </div>
          </div>
        ) : (
          <span style={{ color: PreggaColors.neutral400 }}>—</span>
        );
      },
    },
    {
      key: "subscriptions",
      label: "Subscription",
      render: (_, row) => {
        const activeSub = row.subscriptions?.find(s => s.status === 'active');
        return activeSub ? (
          <div>
            <div style={{ fontWeight: 500, textTransform: 'capitalize' }}>{activeSub.plan_type?.replace('_', ' ') || 'Premium'}</div>
            <div style={{ fontSize: 12, color: PreggaColors.neutral500 }}>{activeSub.status}</div>
          </div>
        ) : (
          <span style={{ color: PreggaColors.neutral400 }}>No subscription</span>
        );
      },
    },
    {
      key: "created_at",
      label: "Joined",
      render: (_, row) => (
        <span style={{ color: PreggaColors.neutral500, fontSize: 13 }}>{formatTimeAgo(row.created_at)}</span>
      ),
    },
  ];

  const hasActiveFilters = Object.values(filters).some(v => v !== undefined) || searchQuery !== "";

  const clearAllFilters = () => {
    setSearchQuery("");
    setFilters({});
  };

  const renderMobileCard = (user: UserWithProfile) => {
    const week = calculatePregnancyWeek(user.pregnant_profiles?.due_date || null);
    const activeSub = user.subscriptions?.find(s => s.status === 'active');
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: PreggaColors.primary100,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: PreggaColors.primary600,
                fontWeight: 600,
                fontSize: 14,
                overflow: "hidden",
              }}
            >
              {user.avatar_url ? (
                <img src={user.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : (
                (user.display_name || "U").split(" ").map((n) => n[0]).join("").slice(0, 2)
              )}
            </div>
            <div>
              <div style={{ fontWeight: 500, fontSize: 14, color: PreggaColors.neutral900 }}>
                {user.display_name || "Unnamed"}
              </div>
              <div style={{ fontSize: 12, color: PreggaColors.neutral500 }}>{user.email || user.phone}</div>
            </div>
          </div>
          {activeSub && <StatusBadge status="active" />}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
          <span style={{ color: PreggaColors.neutral500 }}>{week ? `Week ${week}` : "No pregnancy data"}</span>
          <span style={{ color: PreggaColors.neutral500 }}>
            {user.pregnant_profiles?.due_date ? `Due: ${formatDate(user.pregnant_profiles.due_date)}` : ""}
          </span>
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <AlertCircle size={48} color={PreggaColors.error500} style={{ marginBottom: 16 }} />
        <h3 style={{ color: PreggaColors.neutral900, marginBottom: 8 }}>Failed to load users</h3>
        <p style={{ color: PreggaColors.neutral500 }}>{friendlyError(error)}</p>
        <Button onClick={refetch} style={{ marginTop: 16 }}>Try Again</Button>
      </div>
    );
  }

  // Columns for deletion requests (user-initiated, pending)
  const requestColumns: TableColumn<DeletionRequest & { user?: UserWithProfile }>[] = [
    {
      key: "user",
      label: "User",
      width: "35%",
      render: (_, row) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: "50%", background: PreggaColors.primary100, display: "flex", alignItems: "center", justifyContent: "center", color: PreggaColors.primary500, fontSize: 13, fontWeight: 600, overflow: "hidden", flexShrink: 0 }}>
            {row.user?.avatar_url ? (
              <img src={row.user.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              (row.user?.display_name || "U").split(" ").map((n) => n[0]).join("").slice(0, 2)
            )}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 500, color: PreggaColors.neutral900, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.user?.display_name || "Unnamed"}</div>
            <div style={{ fontSize: 12, color: PreggaColors.neutral500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.user?.email || row.user_email || "No contact"}</div>
          </div>
        </div>
      ),
    },
    {
      key: "reason",
      label: "Reason",
      width: "25%",
      render: (_, row) => <span style={{ color: PreggaColors.neutral600, fontSize: 13 }}>{row.reason || "User requested"}</span>,
    },
    {
      key: "requested_at",
      label: "Requested",
      width: "15%",
      render: (_, row) => (
        <span style={{ color: PreggaColors.neutral500, fontSize: 13 }}>{formatTimeAgo(row.requested_at)}</span>
      ),
    },
    {
      key: "actions",
      label: "",
      width: "25%",
      align: "right",
      render: (_, row) => (
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              if (row.user) onNavigateToSubView?.(row.user.id);
            }}
            icon={<Eye size={14} />}
          >
            View
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              if (row.user) handleApproveRequest(row.id, row.user.id);
            }}
            disabled={isApproving === row.id || !row.user}
            icon={<Check size={14} />}
          >
            {isApproving === row.id ? "..." : "Approve"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleRejectRequest(row.id);
            }}
            disabled={isRejecting === row.id}
            icon={<X size={14} />}
          >
            {isRejecting === row.id ? "..." : "Reject"}
          </Button>
        </div>
      ),
    },
  ];

  const renderRequestMobileCard = (req: DeletionRequest & { user?: UserWithProfile }) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 40, height: 40, borderRadius: "50%", background: PreggaColors.primary100, display: "flex", alignItems: "center", justifyContent: "center", color: PreggaColors.primary500, fontWeight: 600, fontSize: 14, overflow: "hidden" }}>
            {req.user?.avatar_url ? (
              <img src={req.user.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              (req.user?.display_name || "U").split(" ").map((n) => n[0]).join("").slice(0, 2)
            )}
          </div>
          <div>
            <div style={{ fontWeight: 500, fontSize: 14, color: PreggaColors.neutral900 }}>{req.user?.display_name || "Unnamed"}</div>
            <div style={{ fontSize: 12, color: PreggaColors.neutral500 }}>{req.user?.email || req.user_email}</div>
          </div>
        </div>
        <StatusBadge status="pending" />
      </div>
      <div style={{ fontSize: 13, color: PreggaColors.neutral500 }}>{req.reason || "User requested account deletion"}</div>
      <div style={{ display: "flex", gap: 8 }}>
        <Button variant="outline" size="sm" onClick={() => req.user && onNavigateToSubView?.(req.user.id)} icon={<Eye size={14} />}>View</Button>
        <Button variant="primary" size="sm" onClick={() => req.user && handleApproveRequest(req.id, req.user.id)} disabled={isApproving === req.id || !req.user} icon={<Check size={14} />}>Approve</Button>
        <Button variant="outline" size="sm" onClick={() => handleRejectRequest(req.id)} disabled={isRejecting === req.id} icon={<X size={14} />}>Reject</Button>
      </div>
    </div>
  );

  return (
    <>
      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Tabs */}
        <div style={{ display: "flex", gap: 0, borderBottom: `1px solid ${PreggaColors.neutral200}` }}>
          <button
            onClick={() => setListTab("active")}
            style={{
              padding: "12px 20px",
              border: "none",
              borderBottom: listTab === "active" ? `2px solid ${PreggaColors.accent500}` : "2px solid transparent",
              background: "transparent",
              color: listTab === "active" ? PreggaColors.accent700 : PreggaColors.neutral500,
              fontSize: 14,
              fontWeight: listTab === "active" ? 600 : 400,
              cursor: "pointer",
              fontFamily: "inherit",
              marginBottom: -1,
            }}
          >
            Active
          </button>
          <button
            onClick={() => setListTab("requests")}
            style={{
              padding: "12px 20px",
              border: "none",
              borderBottom: listTab === "requests" ? `2px solid ${PreggaColors.accent500}` : "2px solid transparent",
              background: "transparent",
              color: listTab === "requests" ? PreggaColors.accent700 : PreggaColors.neutral500,
              fontSize: 14,
              fontWeight: listTab === "requests" ? 600 : 400,
              cursor: "pointer",
              fontFamily: "inherit",
              marginBottom: -1,
            }}
          >
            Deletion Requests ({filteredDeletionRequests.length})
          </button>
        </div>

        {/* Filters */}
        {listTab === "active" && isMobile ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ flex: 1 }}>
                <Input
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  icon={<Search size={16} />}
                  showClear
                  onClear={() => setSearchQuery("")}
                  style={{ marginBottom: 0 }}
                />
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ flex: 1 }}>
                <Select
                  value={filters.hasSubscription === true ? "yes" : filters.hasSubscription === false ? "no" : ""}
                  onChange={(v) => setFilters({ ...filters, hasSubscription: v === "yes" ? true : v === "no" ? false : undefined })}
                  options={[
                    { value: "", label: "All" },
                    { value: "yes", label: "Subscribed" },
                    { value: "no", label: "Free" },
                  ]}
                />
              </div>
              {hasActiveFilters && (
                <button
                  onClick={clearAllFilters}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 42,
                    height: 42,
                    borderRadius: 8,
                    border: `1px solid ${PreggaColors.neutral200}`,
                    background: PreggaColors.white,
                    color: PreggaColors.neutral500,
                    cursor: "pointer",
                  }}
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>
        ) : listTab === "active" ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 280 }}>
              <Input
                placeholder="Search by name, email, or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                icon={<Search size={16} />}
                showClear
                onClear={() => setSearchQuery("")}
                style={{ marginBottom: 0 }}
              />
            </div>
            <div style={{ width: 150 }}>
              <Select
                value={filters.hasSubscription === true ? "yes" : filters.hasSubscription === false ? "no" : ""}
                onChange={(v) => setFilters({ ...filters, hasSubscription: v === "yes" ? true : v === "no" ? false : undefined })}
                options={[
                  { value: "", label: "All Users" },
                  { value: "yes", label: "Subscribed" },
                  { value: "no", label: "Free" },
                ]}
              />
            </div>
            {hasActiveFilters && (
              <button
                onClick={clearAllFilters}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  height: 42,
                  padding: "0 14px",
                  borderRadius: 8,
                  border: `1px solid ${PreggaColors.neutral200}`,
                  background: PreggaColors.white,
                  color: PreggaColors.neutral700,
                  fontSize: 14,
                  fontFamily: "'Inter', sans-serif",
                  cursor: "pointer",
                }}
              >
                <X size={14} />
                Clear
              </button>
            )}
          </div>
        ) : null}

        {/* Data Table */}
        {listTab === "active" ? (
          <DataTable
            columns={columns}
            data={users}
            currentPage={page}
            totalPages={totalPages}
            totalItems={count}
            pageSize={pageSize}
            onPageChange={setPage}
            onRowClick={(row) => onNavigateToSubView?.(row.id)}
            emptyMessage="No users found"
            isMobile={isMobile}
            mobileCardRender={renderMobileCard}
            isLoading={isLoading}
          />
        ) : (
          <DataTable
            columns={requestColumns}
            data={filteredDeletionRequests}
            currentPage={1}
            totalPages={1}
            totalItems={filteredDeletionRequests.length}
            pageSize={50}
            onPageChange={() => {}}
            emptyMessage="No pending deletion requests"
            isMobile={isMobile}
            mobileCardRender={renderRequestMobileCard}
            isLoading={requestsLoading}
          />
        )}
      </div>
    </>
  );
}

function UserDetailView({
  userId,
  isMobile,
  onGoBack,
  onRefresh,
  onNavigateToConversation,
}: {
  userId: string;
  isMobile: boolean;
  onGoBack?: () => void;
  onRefresh?: () => void;
  onNavigateToConversation?: (conversationId: string) => void;
}) {
  const [activeTab, setActiveTab] = useState<"profile" | "pregnancy" | "subscription" | "conversations" | "broadcasts" | "delete">("profile");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  
  // Subscription activation state
  const [showActivateSubModal, setShowActivateSubModal] = useState(false);
  const [activatingPlan, setActivatingPlan] = useState<SubscriptionPlan>("monthly");
  const [activatingExpiration, setActivatingExpiration] = useState("");
  const [isActivatingSub, setIsActivatingSub] = useState(false);
  
  // Subscription edit state
  const [showEditSubModal, setShowEditSubModal] = useState(false);
  const [editingSubId, setEditingSubId] = useState<string | null>(null);
  const [editSubStatus, setEditSubStatus] = useState<"active" | "cancelled" | "expired" | "trial">("active");
  const [editSubPlan, setEditSubPlan] = useState<SubscriptionPlan>("monthly");
  const [editSubExpiration, setEditSubExpiration] = useState("");
  const [isEditingSub, setIsEditingSub] = useState(false);

  const { data: user, isLoading, error, refetch } = useSupabaseQuery<UserWithProfile | null>(
    ['user', userId],
    () => fetchUserById(userId)
  );

  const { data: conversations } = useSupabaseQuery<ConversationWithUsers[]>(
    ['user', userId, 'conversations'],
    () => fetchUserConversations(userId),
    { enabled: activeTab === 'conversations' }
  );

  const { data: broadcasts } = useSupabaseQuery<BroadcastRequest[]>(
    ['user', userId, 'broadcasts'],
    () => fetchUserBroadcasts(userId),
    { enabled: activeTab === 'broadcasts' }
  );

  const handleDeleteUser = async () => {
    if (!user) return;
    setIsDeleting(true);
    try {
      await deleteUser(user.id);
      toast.success("User permanently deleted");
      setShowDeleteModal(false);
      onRefresh?.();
      onGoBack?.();
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleActivateSubscription = async () => {
    if (!user || !activatingExpiration) return;
    setIsActivatingSub(true);
    try {
      await createManualSubscription(user.id, activatingPlan, activatingExpiration);
      toast.success("Subscription activated successfully");
      setShowActivateSubModal(false);
      setActivatingPlan("monthly");
      setActivatingExpiration("");
      refetch();
      onRefresh?.();
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setIsActivatingSub(false);
    }
  };

  const handleEditSubscription = async () => {
    if (!editingSubId || !editSubExpiration) return;
    setIsEditingSub(true);
    try {
      await updateSubscription(editingSubId, {
        status: editSubStatus,
        plan_type: editSubPlan,
        expiration_at: editSubExpiration,
      });
      toast.success("Subscription updated successfully");
      setShowEditSubModal(false);
      setEditingSubId(null);
      refetch();
      onRefresh?.();
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setIsEditingSub(false);
    }
  };

  const getDefaultExpiration = (plan: SubscriptionPlan): string => {
    const date = new Date();
    switch (plan) {
      case "monthly":
        date.setMonth(date.getMonth() + 1);
        break;
      case "six_months":
        date.setMonth(date.getMonth() + 6);
        break;
      case "yearly":
        date.setFullYear(date.getFullYear() + 1);
        break;
    }
    // Use local date format to avoid timezone shifts
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const openActivateModal = () => {
    setActivatingPlan("monthly");
    setActivatingExpiration(getDefaultExpiration("monthly"));
    setShowActivateSubModal(true);
  };

  if (isLoading) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <Shimmer width={32} height={32} borderRadius={8} />
          <div style={{ flex: 1 }}>
            <Shimmer width={180} height={24} />
            <div style={{ marginTop: 6 }}><Shimmer width={140} height={14} /></div>
          </div>
        </div>
        <Shimmer width="100%" height={140} borderRadius={16} />
        <Shimmer width="100%" height={48} borderRadius={12} />
        <Shimmer width="100%" height={280} borderRadius={16} />
      </div>
    );
  }

  if (error || !user) {
    return (
      <div style={{ padding: 60, textAlign: "center" }}>
        <AlertCircle size={48} color={PreggaColors.error500} style={{ marginBottom: 16 }} />
        <h3 style={{ color: PreggaColors.neutral900, marginBottom: 8 }}>User not found</h3>
        <Button onClick={onGoBack} style={{ marginTop: 16 }}>Go Back</Button>
      </div>
    );
  }

  const pregnancyWeek = calculatePregnancyWeek(user.pregnant_profiles?.due_date || null);
  const activeSub = user.subscriptions?.find(s => s.status === 'active');
  const initials = (user.display_name || "U").split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();

  const tabs = [
    { id: "profile", label: "Profile", icon: <User size={15} /> },
    { id: "pregnancy", label: "Pregnancy", icon: <Calendar size={15} /> },
    { id: "subscription", label: "Subscription", icon: <CreditCard size={15} /> },
    { id: "conversations", label: "Conversations", icon: <MessageCircle size={15} /> },
    { id: "broadcasts", label: "Broadcasts", icon: <Radio size={15} /> },
    { id: "delete", label: "Delete", icon: <Trash2 size={15} /> },
  ] as const;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {/* Header Card */}
      <Card padding="0" style={{ overflow: "hidden" }}>
        {/* Top gradient accent */}
        <div style={{ height: 4, background: `linear-gradient(90deg, ${PreggaColors.accent500} 0%, ${PreggaColors.accent400} 100%)` }} />
        
        <div style={{ padding: isMobile ? "20px" : "24px 28px" }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
            {/* User info section */}
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <button
                onClick={onGoBack}
                style={{
                  background: PreggaColors.neutral100,
                  border: "none",
                  cursor: "pointer",
                  color: PreggaColors.neutral600,
                  padding: 8,
                  borderRadius: 8,
                  display: "flex",
                  transition: "all 0.15s ease",
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = PreggaColors.neutral200}
                onMouseLeave={(e) => e.currentTarget.style.background = PreggaColors.neutral100}
              >
                <ArrowLeft size={18} />
              </button>
              
              {/* Avatar */}
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  background: `linear-gradient(135deg, ${PreggaColors.accent400} 0%, ${PreggaColors.accent500} 100%)`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: PreggaColors.white,
                  fontSize: 18,
                  fontWeight: 600,
                  overflow: "hidden",
                  flexShrink: 0,
                }}
              >
                {user.avatar_url ? (
                  <img src={user.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : initials}
              </div>
              
              <div>
                <h1 style={{ fontSize: 20, fontWeight: 600, color: PreggaColors.neutral900, margin: 0, lineHeight: 1.3 }}>
                  {user.display_name || "Unnamed User"}
                </h1>
                <p style={{ fontSize: 14, color: PreggaColors.neutral500, margin: "4px 0 0 0" }}>
                  {user.email || user.phone || "No contact info"}
                </p>
              </div>
            </div>
            
            {/* Edit button */}
            <Button icon={<Pencil size={15} />} onClick={() => setShowEditModal(true)} size="sm">
              Edit
            </Button>
          </div>
          
          {/* Stats row */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
              gap: isMobile ? 12 : 0,
              marginTop: 24,
              padding: isMobile ? 0 : "0 8px",
            }}
          >
            <StatItem
              label="Pregnancy Week"
              value={pregnancyWeek ? `Week ${pregnancyWeek}` : "Not set"}
              highlight={!!pregnancyWeek}
              isMobile={isMobile}
            />
            <StatItem
              label="Subscription"
              value={activeSub?.plan_type?.replace('_', ' ') || "Free"}
              highlight={!!activeSub}
              isMobile={isMobile}
              center={!isMobile}
            />
            <StatItem
              label="Member Since"
              value={formatDate(user.created_at)}
              isMobile={isMobile}
              right={!isMobile}
            />
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <TabSelector
        tabs={tabs as unknown as Tab[]}
        activeTab={activeTab}
        onTabChange={(tabId) => setActiveTab(tabId as typeof activeTab)}
        isMobile={isMobile}
      />

      {/* Tab Content */}
      {activeTab === "profile" && (
        <Card padding="0">
          <div style={{ padding: "20px 24px", borderBottom: `1px solid ${PreggaColors.neutral100}` }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: PreggaColors.neutral900 }}>
              Profile Information
            </h3>
          </div>
          <div style={{ padding: 24 }}>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 20 }}>
              <DetailRow label="Full Name" value={user.display_name || "—"} />
              <DetailRow label="Email Address" value={user.email || "—"} />
              <DetailRow label="Phone Number" value={user.phone || "—"} />
              <DetailRow label="Age" value={user.age ? `${user.age} years` : "—"} />
              <DetailRow label="Auth Provider" value={user.auth_provider || "email"} />
              <DetailRow label="Onboarding Status" value={user.onboarding_step === 3 ? "Complete" : user.onboarding_step ? `Step ${user.onboarding_step} of 3` : "Not started"} />
              <DetailRow label="Push Notifications" value={user.push_token ? `Enabled (${user.push_platform || "unknown"})` : "Not registered"} />
              <DetailRow label="Account Created" value={formatDate(user.created_at)} />
              <DetailRow label="Last Updated" value={formatDate(user.updated_at)} />
            </div>
          </div>
        </Card>
      )}

      {activeTab === "pregnancy" && (
        <Card padding="0">
          <div style={{ padding: "20px 24px", borderBottom: `1px solid ${PreggaColors.neutral100}` }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: PreggaColors.neutral900 }}>
              Pregnancy Details
            </h3>
          </div>
          <div style={{ padding: 24 }}>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 20 }}>
              <DetailRow label="Due Date" value={user.pregnant_profiles?.due_date ? formatDate(user.pregnant_profiles.due_date) : "Not set"} />
              <DetailRow label="Current Week" value={pregnancyWeek ? `Week ${pregnancyWeek}` : "—"} />
              <DetailRow label="Trimester" value={pregnancyWeek ? (pregnancyWeek <= 13 ? "First Trimester" : pregnancyWeek <= 26 ? "Second Trimester" : "Third Trimester") : "—"} />
              <DetailRow label="Trying to Conceive" value={user.pregnant_profiles?.is_trying_to_conceive ? "Yes" : "No"} />
            </div>
          </div>
        </Card>
      )}

      {activeTab === "subscription" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <Card padding="0">
            <div style={{ padding: "20px 24px", borderBottom: `1px solid ${PreggaColors.neutral100}` }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: PreggaColors.neutral900 }}>
                Current Subscription
              </h3>
            </div>
            {activeSub ? (
              <div style={{ padding: 24 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
                  <div>
                    <div style={{ fontSize: 12, color: PreggaColors.neutral500, marginBottom: 6, textTransform: "uppercase", fontWeight: 500, letterSpacing: "0.5px" }}>Plan</div>
                    <div style={{ fontSize: 26, fontWeight: 700, color: PreggaColors.accent600, textTransform: "capitalize" }}>
                      {activeSub.plan_type?.replace('_', ' ') || "Premium"}
                    </div>
                  </div>
                  <StatusBadge status={activeSub.status ?? "expired"} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12, marginBottom: 20 }}>
                  <div style={{ padding: 16, background: PreggaColors.neutral50, borderRadius: 10 }}>
                    <div style={{ fontSize: 12, color: PreggaColors.neutral500, marginBottom: 4 }}>Start Date</div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: PreggaColors.neutral900 }}>{activeSub.purchased_at ? formatDate(activeSub.purchased_at) : "—"}</div>
                  </div>
                  <div style={{ padding: 16, background: PreggaColors.neutral50, borderRadius: 10 }}>
                    <div style={{ fontSize: 12, color: PreggaColors.neutral500, marginBottom: 4 }}>Renewal Date</div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: PreggaColors.neutral900 }}>{activeSub.expiration_at ? formatDate(activeSub.expiration_at) : "Auto-renewing"}</div>
                  </div>
                  <div style={{ padding: 16, background: PreggaColors.neutral50, borderRadius: 10, gridColumn: isMobile ? "1" : "1 / -1" }}>
                    <div style={{ fontSize: 12, color: PreggaColors.neutral500, marginBottom: 4 }}>Source</div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: PreggaColors.neutral900 }}>
                      {activeSub.store === 'manual' ? 'Admin (Manual)' : 
                       activeSub.store === 'APP_STORE' ? 'App Store' : 
                       activeSub.store === 'PLAY_STORE' ? 'Play Store' : 
                       activeSub.store || 'Unknown'}
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  <Button icon={<Pencil size={16} />} onClick={() => {
                    setEditingSubId(activeSub.id);
                    setEditSubStatus(activeSub.status || "active");
                    setEditSubPlan(activeSub.plan_type || "monthly");
                    setEditSubExpiration(activeSub.expiration_at ? activeSub.expiration_at.split("T")[0] : "");
                    setShowEditSubModal(true);
                  }}>
                    Edit Subscription
                  </Button>
                </div>
              </div>
            ) : (
              <div style={{ padding: "48px 24px", textAlign: "center" }}>
                <div style={{ width: 48, height: 48, borderRadius: 12, background: PreggaColors.neutral100, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
                  <CreditCard size={24} color={PreggaColors.neutral400} />
                </div>
                <div style={{ fontSize: 15, fontWeight: 500, color: PreggaColors.neutral600 }}>No active subscription</div>
                <div style={{ fontSize: 13, color: PreggaColors.neutral400, marginTop: 4, marginBottom: 16 }}>User is on the free plan</div>
                <Button icon={<Plus size={16} />} onClick={openActivateModal}>
                  Activate Subscription
                </Button>
              </div>
            )}
          </Card>

          {user.subscriptions && user.subscriptions.length > 1 && (
            <Card padding="0">
              <div style={{ padding: "20px 24px", borderBottom: `1px solid ${PreggaColors.neutral100}` }}>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: PreggaColors.neutral900 }}>
                  Subscription History
                </h3>
              </div>
              <div style={{ padding: 16 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {user.subscriptions.filter(s => s.id !== activeSub?.id).map((sub) => (
                    <div key={sub.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", background: PreggaColors.neutral50, borderRadius: 8 }}>
                      <div>
                        <div style={{ fontWeight: 500, textTransform: "capitalize", fontSize: 14 }}>{sub.plan_type?.replace('_', ' ') || "Unknown"}</div>
                        <div style={{ fontSize: 12, color: PreggaColors.neutral500, marginTop: 2 }}>
                          {sub.purchased_at ? formatDate(sub.purchased_at) : "—"} — {sub.expiration_at ? formatDate(sub.expiration_at) : "Ongoing"}
                        </div>
                      </div>
                      <StatusBadge status={sub.status ?? "expired"} />
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          )}
        </div>
      )}

      {activeTab === "conversations" && (
        <Card padding="0">
          <div style={{ padding: "20px 24px", borderBottom: `1px solid ${PreggaColors.neutral100}` }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: PreggaColors.neutral900 }}>
              Conversation History
            </h3>
          </div>
          {conversations && conversations.length > 0 ? (
            <div style={{ padding: 16 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {conversations.map((conv) => (
                  <div
                    key={conv.id}
                    onClick={() => onNavigateToConversation?.(conv.id)}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: 16,
                      background: PreggaColors.neutral50,
                      borderRadius: 10,
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = PreggaColors.neutral100}
                    onMouseLeave={(e) => e.currentTarget.style.background = PreggaColors.neutral50}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ width: 40, height: 40, borderRadius: 10, background: PreggaColors.accent100, display: "flex", alignItems: "center", justifyContent: "center", color: PreggaColors.accent600 }}>
                        <MessageCircle size={18} />
                      </div>
                      <div>
                        <div style={{ fontWeight: 500, fontSize: 14 }}>Chat with {conv.doula?.display_name || "Doula"}</div>
                        <div style={{ fontSize: 12, color: PreggaColors.neutral500, marginTop: 2 }}>
                          Started {formatTimeAgo(conv.started_at || "")}
                        </div>
                      </div>
                    </div>
                    <StatusBadge status={conv.is_active ? "active" : "ended"} />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ padding: "48px 24px", textAlign: "center" }}>
              <div style={{ width: 48, height: 48, borderRadius: 12, background: PreggaColors.neutral100, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
                <MessageCircle size={24} color={PreggaColors.neutral400} />
              </div>
              <div style={{ fontSize: 15, fontWeight: 500, color: PreggaColors.neutral600 }}>No conversations yet</div>
              <div style={{ fontSize: 13, color: PreggaColors.neutral400, marginTop: 4 }}>User hasn't started any chats</div>
            </div>
          )}
        </Card>
      )}

      {activeTab === "broadcasts" && (
        <Card padding="0">
          <div style={{ padding: "20px 24px", borderBottom: `1px solid ${PreggaColors.neutral100}` }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: PreggaColors.neutral900 }}>
              Broadcast Requests
            </h3>
          </div>
          {broadcasts && broadcasts.length > 0 ? (
            <div style={{ padding: 16 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {broadcasts.map((broadcast) => (
                  <div
                    key={broadcast.id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      padding: 16,
                      background: PreggaColors.neutral50,
                      borderRadius: 10,
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500, marginBottom: 4, fontSize: 14, lineHeight: 1.4 }}>
                        {broadcast.initial_message?.slice(0, 80) || "No message"}
                        {broadcast.initial_message && broadcast.initial_message.length > 80 ? "..." : ""}
                      </div>
                      <div style={{ fontSize: 12, color: PreggaColors.neutral500 }}>
                        {formatTimeAgo(broadcast.created_at)} • {broadcast.notified_doula_ids?.length || 0} doulas notified
                      </div>
                    </div>
                    <StatusBadge status={broadcast.status} />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ padding: "48px 24px", textAlign: "center" }}>
              <div style={{ width: 48, height: 48, borderRadius: 12, background: PreggaColors.neutral100, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
                <Radio size={24} color={PreggaColors.neutral400} />
              </div>
              <div style={{ fontSize: 15, fontWeight: 500, color: PreggaColors.neutral600 }}>No broadcast requests</div>
              <div style={{ fontSize: 13, color: PreggaColors.neutral400, marginTop: 4 }}>User hasn't sent any broadcasts</div>
            </div>
          )}
        </Card>
      )}

      {activeTab === "delete" && (
        <Card padding="0">
          <div style={{ padding: "20px 24px", borderBottom: `1px solid ${PreggaColors.neutral100}` }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: PreggaColors.error600 }}>
              Danger Zone
            </h3>
          </div>
          <div style={{ padding: 24 }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 16, padding: 20, background: PreggaColors.error50, borderRadius: 12, border: `1px solid ${PreggaColors.error100}` }}>
              <div style={{ width: 44, height: 44, borderRadius: 10, background: PreggaColors.white, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <AlertTriangle size={22} color={PreggaColors.error500} />
              </div>
              <div style={{ flex: 1 }}>
                <h4 style={{ fontSize: 15, fontWeight: 600, color: PreggaColors.neutral900, margin: "0 0 6px" }}>
                  Delete User Account
                </h4>
                <p style={{ fontSize: 13, color: PreggaColors.neutral600, margin: "0 0 16px", lineHeight: 1.5 }}>
                  This will create a deletion request for this user. All associated data including pregnancy info, conversations, and subscription history will be scheduled for permanent removal. This action cannot be undone.
                </p>
                <button
                  onClick={() => setShowDeleteModal(true)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "8px 14px",
                    fontSize: 13,
                    fontWeight: 500,
                    borderRadius: 8,
                    border: `1px solid ${PreggaColors.error400}`,
                    color: PreggaColors.error600,
                    background: PreggaColors.white,
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = PreggaColors.error50;
                    e.currentTarget.style.borderColor = PreggaColors.error500;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = PreggaColors.white;
                    e.currentTarget.style.borderColor = PreggaColors.error400;
                  }}
                >
                  <Trash2 size={14} />
                  Delete User
                </button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Edit Modal */}
      <EditUserModal
        user={user}
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSave={async (data) => {
          try {
            await updateUserProfile(user.id, data.profile, data.pregnant ?? undefined);
            toast.success("Profile updated");
            refetch();
            setShowEditModal(false);
          } catch (err) {
            toast.error(friendlyError(err));
          }
        }}
      />

      {/* Delete Modal with DELETE verification */}
      <Modal
        open={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setDeleteConfirmText(""); }}
        title=""
        width={420}
        footer={
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
            <Button variant="outline" onClick={() => { setShowDeleteModal(false); setDeleteConfirmText(""); }}>Cancel</Button>
            <Button
              onClick={handleDeleteUser}
              loading={isDeleting}
              disabled={deleteConfirmText !== "DELETE"}
              style={{ 
                background: deleteConfirmText === "DELETE" ? PreggaColors.error500 : PreggaColors.neutral300,
                cursor: deleteConfirmText === "DELETE" ? "pointer" : "not-allowed",
              }}
            >
              Delete User
            </Button>
          </div>
        }
      >
        <div style={{ textAlign: "center", padding: "8px 0" }}>
          <div style={{ width: 56, height: 56, borderRadius: 14, background: PreggaColors.error50, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
            <AlertTriangle size={28} color={PreggaColors.error500} />
          </div>
          <h3 style={{ fontSize: 18, fontWeight: 600, color: PreggaColors.neutral900, margin: "0 0 8px" }}>
            Delete User Account?
          </h3>
          <p style={{ fontSize: 14, color: PreggaColors.neutral500, lineHeight: 1.6, margin: "0 0 20px" }}>
            Are you sure you want to delete <strong style={{ color: PreggaColors.neutral700 }}>{user.display_name || "this user"}</strong>?
            This will create a deletion request and schedule the account for permanent removal.
          </p>
          <div style={{ textAlign: "left" }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: PreggaColors.neutral700, marginBottom: 8 }}>
              Type <span style={{ fontFamily: "monospace", background: PreggaColors.neutral100, padding: "2px 6px", borderRadius: 4, color: PreggaColors.error600 }}>DELETE</span> to confirm
            </label>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="DELETE"
              style={{
                width: "100%",
                padding: "12px 14px",
                fontSize: 14,
                border: `1px solid ${deleteConfirmText === "DELETE" ? PreggaColors.error400 : PreggaColors.neutral200}`,
                borderRadius: 8,
                outline: "none",
                fontFamily: "monospace",
                letterSpacing: "1px",
                transition: "border-color 0.15s ease, box-shadow 0.15s ease",
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = PreggaColors.error400;
                e.currentTarget.style.boxShadow = `0 0 0 3px ${PreggaColors.error100}`;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = deleteConfirmText === "DELETE" ? PreggaColors.error400 : PreggaColors.neutral200;
                e.currentTarget.style.boxShadow = "none";
              }}
            />
          </div>
        </div>
      </Modal>

      {/* Activate Subscription Modal */}
      <Modal
        open={showActivateSubModal}
        onClose={() => setShowActivateSubModal(false)}
        title="Activate Subscription"
        width={420}
        footer={
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
            <Button variant="outline" onClick={() => setShowActivateSubModal(false)}>Cancel</Button>
            <Button
              onClick={handleActivateSubscription}
              loading={isActivatingSub}
              disabled={!activatingExpiration}
            >
              Activate
            </Button>
          </div>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <div>
            <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: PreggaColors.neutral700, marginBottom: 8 }}>
              Plan Type
            </label>
            <Select
              value={activatingPlan}
              onChange={(val) => {
                const plan = val as SubscriptionPlan;
                setActivatingPlan(plan);
                setActivatingExpiration(getDefaultExpiration(plan));
              }}
              options={[
                { value: "monthly", label: "Monthly" },
                { value: "six_months", label: "6 Months" },
                { value: "yearly", label: "Yearly" },
              ]}
            />
          </div>
          
          <div>
            <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: PreggaColors.neutral700, marginBottom: 8 }}>
              Expiration Date
            </label>
            <Input
              type="date"
              value={activatingExpiration}
              onChange={(e) => setActivatingExpiration(e.target.value)}
              min={toLocalDateString(new Date())}
            />
          </div>

          <div style={{ 
            padding: 12, 
            background: PreggaColors.accent50, 
            borderRadius: 8, 
            fontSize: 13, 
            color: PreggaColors.accent700,
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
          }}>
            <CreditCard size={16} style={{ marginTop: 2, flexShrink: 0 }} />
            <div>
              <strong>Quick Presets:</strong>
              <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                {[
                  { label: "+1 Month", plan: "monthly" as SubscriptionPlan },
                  { label: "+6 Months", plan: "six_months" as SubscriptionPlan },
                  { label: "+1 Year", plan: "yearly" as SubscriptionPlan },
                ].map((preset) => (
                  <button
                    key={preset.plan}
                    onClick={() => {
                      setActivatingPlan(preset.plan);
                      setActivatingExpiration(getDefaultExpiration(preset.plan));
                    }}
                    style={{
                      padding: "4px 10px",
                      fontSize: 12,
                      fontWeight: 500,
                      background: activatingPlan === preset.plan ? PreggaColors.accent500 : PreggaColors.white,
                      color: activatingPlan === preset.plan ? PreggaColors.white : PreggaColors.accent600,
                      border: `1px solid ${PreggaColors.accent300}`,
                      borderRadius: 6,
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                    }}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div style={{ 
            padding: 12, 
            background: PreggaColors.neutral50, 
            borderRadius: 8, 
            fontSize: 12, 
            color: PreggaColors.neutral600,
          }}>
            This will create a manual subscription with <strong>store: 'manual'</strong>. 
            It will not affect RevenueCat subscriptions.
          </div>
        </div>
      </Modal>

      {/* Edit Subscription Modal */}
      <Modal
        open={showEditSubModal}
        onClose={() => setShowEditSubModal(false)}
        title="Edit Subscription"
        width={420}
        footer={
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
            <Button variant="outline" onClick={() => setShowEditSubModal(false)}>Cancel</Button>
            <Button onClick={handleEditSubscription} loading={isEditingSub} disabled={!editSubExpiration}>
              Save Changes
            </Button>
          </div>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <div>
            <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: PreggaColors.neutral700, marginBottom: 8 }}>
              Status
            </label>
            <Select
              value={editSubStatus}
              onChange={(val) => setEditSubStatus(val as typeof editSubStatus)}
              options={[
                { value: "active", label: "Active" },
                { value: "trial", label: "Trial" },
                { value: "cancelled", label: "Cancelled" },
                { value: "expired", label: "Expired" },
              ]}
            />
          </div>
          
          <div>
            <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: PreggaColors.neutral700, marginBottom: 8 }}>
              Plan Type
            </label>
            <Select
              value={editSubPlan}
              onChange={(val) => setEditSubPlan(val as SubscriptionPlan)}
              options={[
                { value: "monthly", label: "Monthly" },
                { value: "six_months", label: "6 Months" },
                { value: "yearly", label: "Yearly" },
              ]}
            />
          </div>
          
          <div>
            <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: PreggaColors.neutral700, marginBottom: 8 }}>
              Expiration Date
            </label>
            <Input
              type="date"
              value={editSubExpiration}
              onChange={(e) => setEditSubExpiration(e.target.value)}
            />
          </div>

          <div style={{ 
            padding: 12, 
            background: PreggaColors.warning50, 
            borderRadius: 8, 
            fontSize: 12, 
            color: PreggaColors.warning700,
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
          }}>
            <AlertCircle size={16} style={{ marginTop: 1, flexShrink: 0 }} />
            <span>Changes will take effect immediately. The user's access will be updated based on the new status.</span>
          </div>

          {activeSub?.store && activeSub.store !== 'manual' && (
            <div style={{ 
              padding: 12, 
              background: PreggaColors.info50, 
              borderRadius: 8, 
              fontSize: 12, 
              color: PreggaColors.info700,
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
            }}>
              <CreditCard size={16} style={{ marginTop: 1, flexShrink: 0 }} />
              <span>
                <strong>RevenueCat Subscription:</strong> This subscription is managed by {activeSub.store}. 
                Your changes will apply immediately, but RevenueCat webhooks may override them later.
              </span>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}

function StatItem({ label, value, highlight, isMobile, center, right }: { 
  label: string; 
  value: string; 
  highlight?: boolean;
  isMobile?: boolean;
  center?: boolean;
  right?: boolean;
}) {
  return (
    <div style={{ 
      textAlign: isMobile ? "left" : center ? "center" : right ? "right" : "left",
      padding: isMobile ? "12px 16px" : 0,
      background: isMobile ? PreggaColors.neutral50 : "transparent",
      borderRadius: isMobile ? 10 : 0,
    }}>
      <div style={{ 
        fontSize: 12, 
        fontWeight: 500, 
        color: PreggaColors.neutral500, 
        textTransform: "uppercase", 
        letterSpacing: "0.5px", 
        marginBottom: 4 
      }}>
        {label}
      </div>
      <div style={{ 
        fontSize: isMobile ? 16 : 18, 
        fontWeight: 600, 
        color: highlight ? PreggaColors.accent600 : PreggaColors.neutral900,
        textTransform: label === "Subscription" ? "capitalize" : "none",
      }}>
        {value}
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ fontSize: 12, fontWeight: 500, color: PreggaColors.neutral500, textTransform: "uppercase", letterSpacing: "0.3px" }}>
        {label}
      </div>
      <div style={{ fontSize: 15, fontWeight: 500, color: PreggaColors.neutral900 }}>
        {value}
      </div>
    </div>
  );
}

function EditUserModal({
  user,
  open,
  onClose,
  onSave,
}: {
  user: UserWithProfile;
  open: boolean;
  onClose: () => void;
  onSave: (data: { profile: Partial<UserWithProfile>; pregnant?: Partial<UserWithProfile['pregnant_profiles']> }) => Promise<void>;
}) {
  const [formData, setFormData] = useState({
    display_name: user.display_name || "",
    email: user.email || "",
    phone: user.phone || "",
    due_date: user.pregnant_profiles?.due_date || "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setFormData({
        display_name: user.display_name || "",
        email: user.email || "",
        phone: user.phone || "",
        due_date: user.pregnant_profiles?.due_date || "",
      });
    }
  }, [open, user]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        profile: {
          display_name: formData.display_name || null,
          email: formData.email || null,
          phone: formData.phone || null,
        },
        pregnant: formData.due_date ? { due_date: formData.due_date } : undefined,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit User Profile"
      width={480}
      footer={
        <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} loading={saving}>Save Changes</Button>
        </div>
      }
    >
      <Input
        label="Name"
        value={formData.display_name}
        onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
      />
      <Input
        label="Email"
        type="email"
        value={formData.email}
        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
      />
      <Input
        label="Phone"
        type="tel"
        value={formData.phone}
        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
      />
      <Input
        label="Due Date"
        type="date"
        value={formData.due_date}
        onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
      />
    </Modal>
  );
}
